/**
 * Aria-label lookup utilities.
 *
 * App-Router-native re-implementation of the legacy aria-label system. Instead
 * of the legacy async loader + mutable module cache + RegionProvider, dictionary
 * data is provided per request (see AriaLabelsProvider) and looked up
 * synchronously here. Brand names in the data are stored as a `{brand}` token and
 * interpolated at lookup time so one dataset serves every brand.
 */

import { getBrandDisplayName } from '@/lib/cms/config';
import type { AriaLabelDictionary } from './types';

const interpolateBrand = (value: string, brand: string): string =>
  value.replace(/\{brand\}/g, brand);

/** Normalize an href to the dictionary key format: leading+trailing slash, locale prefix stripped, lowercased. */
export function normalizeUrl(url: string): string {
  let clean = (url || '').split('?')[0];

  if (clean.startsWith('http')) {
    clean = '/' + clean.split('/').slice(3).join('/');
  }
  if (!clean.startsWith('/')) clean = '/' + clean;
  if (!clean.endsWith('/')) clean = clean + '/';

  // Strip locale prefix (e.g. '/us-en/', '/de-de/') to match dictionary keys.
  clean = clean.replace(/^\/[a-z]{2}-[a-z]{2,3}\//i, '/');

  return clean.toLowerCase();
}

/**
 * Derive a human-readable aria-label from a URL path when it isn't in the
 * dictionary, so every link still gets a meaningful label. Ported from legacy
 * and parameterized by brand.
 */
export function generateFallbackAriaLabel(normalizedUrl: string, brand: string): string {
  let path = normalizedUrl.replace(/^\//, '').replace(/\/$/, '');
  path = path.replace(/^[a-z]{2}-[a-z]{2,3}\//i, '');

  if (!path) return `${brand} home page`;

  const segments = path.split('/').filter(Boolean);
  const firstSegment = segments[0] || '';
  const lastSegment = segments[segments.length - 1] || '';

  const toTitleCase = (str: string): string =>
    str
      .split('-')
      .map((word) => {
        const upperWords = ['nfl', 'faq', 'gmt', 'avi', 'usa', 'uk', 'gb'];
        if (upperWords.includes(word.toLowerCase())) return word.toUpperCase();
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');

  const humanReadable = toTitleCase(lastSegment);

  if (firstSegment === 'collections') {
    if (lastSegment === 'collections' || !lastSegment) return 'All watch collections';
    return `${humanReadable} collection`;
  }
  if (firstSegment === 'watches') {
    if (lastSegment === 'watches' || !lastSegment) return 'Browse all watches';
    if (lastSegment === 'all') return 'All watches';
    if (lastSegment === 'novelties') return 'New watches';
    if (lastSegment === 'limited-editions') return 'Limited edition watches';
    return `${humanReadable} watches`;
  }
  if (firstSegment === 'straps') {
    if (lastSegment === 'straps' || !lastSegment) return 'Browse watch straps';
    if (lastSegment === 'all') return 'All watch straps';
    return `${humanReadable} straps`;
  }
  if (firstSegment === 'service') {
    if (lastSegment === 'service' || !lastSegment) return `${brand} services`;
    if (lastSegment === 'contact') return 'Contact customer service';
    if (lastSegment === 'faq') return 'Frequently asked questions';
    return humanReadable;
  }
  if (firstSegment === 'about') {
    if (lastSegment === 'about' || !lastSegment) return `About ${brand}`;
    if (lastSegment === 'history') return `${brand} history`;
    return humanReadable;
  }
  if (firstSegment === 'my-account') {
    if (lastSegment === 'my-account' || !lastSegment) return 'My account';
    if (lastSegment === 'login') return 'Sign in to your account';
    if (lastSegment === 'my-orders') return 'View your order history';
    if (lastSegment === 'my-wishlist') return 'View your wishlist';
    if (lastSegment === 'my-addresses') return 'Manage your addresses';
    if (lastSegment === 'forgotten-password') return 'Reset your password';
    return humanReadable;
  }
  if (firstSegment === 'stores') return 'Find a boutique near you';
  if (firstSegment === 'privacy-policy') return 'Privacy policy';
  if (firstSegment === 'terms-and-conditions') return 'Terms and conditions';

  return humanReadable;
}

/** Resolve a link's aria-label from the dictionary, falling back to a derived label. */
export function getLinkAriaLabel(
  href: string,
  dict: AriaLabelDictionary,
  brand: string = getBrandDisplayName()
): string {
  const normalized = normalizeUrl(href);
  const mapped = dict.urls?.[normalized];
  if (mapped) return interpolateBrand(mapped, brand);
  return generateFallbackAriaLabel(normalized, brand);
}

function sectionLookup(
  section: Record<string, string> | undefined,
  key: string,
  brand: string
): string | undefined {
  const value = section?.[key];
  return value === undefined ? undefined : interpolateBrand(value, brand);
}

export const getActionAriaLabel = (dict: AriaLabelDictionary, key: string, brand: string) =>
  sectionLookup(dict.actions, key, brand);
export const getFormAriaLabel = (dict: AriaLabelDictionary, key: string, brand: string) =>
  sectionLookup(dict.forms, key, brand);
export const getConstantAriaLabel = (dict: AriaLabelDictionary, key: string, brand: string) =>
  sectionLookup(dict.constants, key, brand);
